package dev.kuml.portal.web.editor

import kotlinx.browser.document
import kotlinx.browser.window
import kotlinx.coroutines.suspendCancellableCoroutine
import org.w3c.dom.CanvasRenderingContext2D
import org.w3c.dom.HTMLAnchorElement
import org.w3c.dom.HTMLCanvasElement
import org.w3c.dom.HTMLImageElement
import org.w3c.dom.url.URL
import org.w3c.files.Blob
import kotlin.coroutines.resume

/** Opaque background for the PNG raster. Deliberately the LIGHT theme's `--kportal-bg` value
 * verbatim rather than the computed CSS variable: an exported file is shared and embedded
 * elsewhere, so it must not silently come out dark-navy just because the exporting user's OS was
 * in dark mode. Same reasoning (and same literal) as `profile/AvatarEncoder.kt`'s white fill. */
private const val PNG_BACKGROUND = "#FFFFFF"

/** Chromium will cancel an in-flight download if its object URL is revoked in the same tick. */
private const val REVOKE_DELAY_MS = 1000

/**
 * Triggers a browser download of an EXISTING blob URL whose lifetime the caller owns -- used for the
 * lossless SVG download, which points straight at the blob URL `PortalApi` already created from the
 * server's SVG response (see `PortalApi.toSvgBlobUrl`). Deliberately does NOT revoke [blobUrl]:
 * that URL is still the live `<img>` source, and `EditorScreen`'s `revokeBlobUrlIfAny` owns its
 * lifecycle.
 *
 * The anchor is built with `document.createElement` and never via `innerHTML` (repo convention),
 * and is removed from the DOM immediately after the synthetic click.
 */
public fun triggerBlobUrlDownload(
    blobUrl: String,
    fileName: String,
) {
    val anchor = document.createElement("a") as HTMLAnchorElement
    anchor.href = blobUrl
    anchor.download = fileName
    anchor.rel = "noopener"
    anchor.style.display = "none"
    document.body?.appendChild(anchor)
    anchor.click()
    anchor.remove()
}

/** [triggerBlobUrlDownload]'s sibling for a blob this function itself owns (the freshly encoded
 * PNG): creates the object URL, downloads it, and revokes it after [REVOKE_DELAY_MS]. */
public fun triggerBlobDownload(
    blob: Blob,
    fileName: String,
) {
    val url = URL.createObjectURL(blob)
    triggerBlobUrlDownload(url, fileName)
    window.setTimeout({ URL.revokeObjectURL(url) }, REVOKE_DELAY_MS)
}

/**
 * Rasterises an ALREADY-LOADED SVG `<img>` (the editor stage's diagram image) to a PNG blob, purely
 * client-side -- no server roundtrip, no second network fetch. Returns `null` on any failure, which
 * the caller surfaces as an error status rather than a silent no-op.
 *
 * Draws the passed element directly instead of loading a fresh `Image()` from the same blob URL:
 * the on-screen element already holds a decoded bitmap, so there is no second load to race against
 * `revokeBlobUrlIfAny()` if the user regenerates the diagram mid-export. The element's CSS state
 * (e.g. the `kportal-stage-dimmed` opacity class, or the zoom wrapper's transform) does not affect
 * `drawImage`, which samples the decoded image, not the painted box.
 *
 * `toBlob` is wrapped defensively: an SVG carrying external references would taint the canvas and
 * make it throw `SecurityError`. kUML's renderer emits fully self-contained SVGs (inline `<style>`,
 * no `foreignObject`), so this should be unreachable -- but a tainted canvas must degrade to an
 * error message, not an unhandled exception.
 *
 * Known, accepted limitation: an SVG referenced from an `<img>` cannot load external web fonts, so
 * text rasterises with locally available fonts. Identical behaviour to `obsidian-kuml`'s PNG export.
 */
public suspend fun rasterizeSvgImageToPng(image: HTMLImageElement): Blob? {
    if (!image.complete) return null
    val size =
        PngExportMath.computeRasterSize(image.naturalWidth.toDouble(), image.naturalHeight.toDouble())
            ?: return null

    val canvas = document.createElement("canvas") as HTMLCanvasElement
    canvas.width = size.width
    canvas.height = size.height
    val ctx = canvas.getContext("2d") as? CanvasRenderingContext2D ?: return null
    ctx.fillStyle = PNG_BACKGROUND
    ctx.fillRect(0.0, 0.0, size.width.toDouble(), size.height.toDouble())

    return suspendCancellableCoroutine { cont ->
        try {
            ctx.drawImage(image, 0.0, 0.0, size.width.toDouble(), size.height.toDouble())
            canvas.toBlob({ blob -> cont.resume(blob) }, "image/png")
        } catch (e: Throwable) {
            cont.resume(null)
        }
    }
}
